import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { CORAL_700, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { FilterType, usePropertyDashboardFilter } from '../_main/context/PropertyDashboardFilter.context'

import Box from '@mui/material/Box'
import { DeleteOutlineOutlined } from '@mui/icons-material'
import { FC } from 'react'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { OrderAssignmentStatusFilter } from 'constants/misc'
import { PropertiesDateFilter } from './PropertyDateFilter.component'
import { ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

/**
 * @component Properties dashboard filter in mobile drawer which contains manager dropdown, date range picker, order status filter and search input.
 * 
 * @example
 * <PropertiesDashboardFilterDrawer />
 */
export const PropertiesDashboardFilterDrawer: FC = () => {

  const { t } = useTranslation(['properties_dashboard'])

  const {
    showManagersInput,
    managersList,
    isDrawerOpen,
    setIsDrawerOpen,
    openFilter,
    setOpenFilter,
    handleCloseFilter,
    selectedOrderStatus,
    disableSearchManager,
    selectedManager,
    getOrderStatusDisplayText,
    handleOrderStatusChange,
    handleRemoveOrderStatus,
    handleChangeManager,
    handleRemoveManager,
    handleClearAllFilters,
    setIsFilterIconActive,
  } = usePropertyDashboardFilter()

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    setIsFilterIconActive(false)
  }

  return (
    <ResponsiveDrawer
      backdrop="dark"
      isOpen={isDrawerOpen}
      onClose={handleCloseDrawer}
      title={t('filters')}
      actionsSlot={
        <Stack width="100%" gap=".8rem" flexDirection="row" justifyContent="space-between">
          <MUIButton
            fullWidth
            type="secondaryNoBorder"
            onClick={() => handleClearAllFilters()}
          >
            {t('clear_all')}
          </MUIButton>

          <MUIButton
            fullWidth
            onClick={() => handleCloseDrawer()}
          >
            {t('apply_filters')}
          </MUIButton>
        </Stack>
      }
    >

      <Stack marginBottom="1.6rem">

        {/* Property Creation Date Filter */}
        <Stack gap=".6rem">
          <Typography variant="text-sm" color={GRAY_700} fontWeight="medium">
            {t('by_creation_date')}
          </Typography>

          <PropertiesDateFilter />
        </Stack>

        <MUIDivider margin={16} />

        {/* Order Status Filter */}
        <Stack gap=".6rem">
          <Typography variant="text-sm" color={GRAY_700} fontWeight="medium">
            {t('by_status')}
          </Typography>

          <MUIDropdown
            open={openFilter === FilterType.ORDER_STATUS}
            onClose={handleCloseFilter}
            button={(isOpen, action, onClose) => (
              <MUIDropdownInput
                readOnly={true}
                value={selectedOrderStatus ? getOrderStatusDisplayText(selectedOrderStatus) : ''}
                placeholder={t('order_status')}
                type={DropdownInputTypes.SELECTOR}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1.6rem',
                    fontWeight: 400,
                  }
                }}
                onClick={(e) => {
                  logAnalyticsEvent(AnalyticsEvent.FILTER_PROPERTY, { filterType: FilterType.ORDER_STATUS })
                  setOpenFilter(FilterType.ORDER_STATUS)
                  action(e)
                }}
                customInputContent={!!selectedOrderStatus &&
                  <Stack
                    flexDirection="row"
                    paddingRight="0.4rem"
                    fontSize="1.4rem"
                    alignItems="center"
                    paddingTop="0.1rem"
                    color={GRAY_900}
                    onClick={handleRemoveOrderStatus}
                    sx={{ cursor: 'pointer', ':hover': { color: CORAL_700 } }}
                  >
                    <DeleteOutlineOutlined fontSize="inherit" />
                  </Stack>
                }
              />
            )}
          >
            <Box width="16rem">
              <MUIDropdownItem onClick={() => handleOrderStatusChange('')}>
                <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                  {t('all')}
                </Typography>
              </MUIDropdownItem>
              <MUIDropdownItem onClick={() => handleOrderStatusChange(OrderAssignmentStatusFilter.ONGOING)}>
                <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                  {t('ongoing')}
                </Typography>
              </MUIDropdownItem>
              <MUIDropdownItem onClick={() => handleOrderStatusChange(OrderAssignmentStatusFilter.NEEDS_ATTENTION)}>
                <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                  {t('needs_attention')}
                </Typography>
              </MUIDropdownItem>
              <MUIDropdownItem onClick={() => handleOrderStatusChange(OrderAssignmentStatusFilter.DELIVERED)}>
                <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                  {t('delivered')}
                </Typography>
              </MUIDropdownItem>
              <MUIDropdownItem onClick={() => handleOrderStatusChange(OrderAssignmentStatusFilter.CANCELLED)}>
                <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                  {t('cancelled')}
                </Typography>
              </MUIDropdownItem>
            </Box>
          </MUIDropdown>
        </Stack>

        <MUIDivider margin={16} />

        {/* Manager Filter - Only render if managers exist */}
        {showManagersInput && (
          <Stack gap=".6rem">
            <Typography variant="text-sm" color={GRAY_700} fontWeight="medium">
              {t('by_manager')}
            </Typography>

            <MUIDropdown
              open={openFilter === FilterType.MANAGER}
              onClose={handleCloseFilter}
              button={(isOpen, action, onClose) => (
                <MUIDropdownInput
                  readOnly={disableSearchManager}
                  value={selectedManager ?? ''}
                  onChange={e => handleChangeManager(e.target.value)}
                  placeholder={t('manager')}
                  type={DropdownInputTypes.SELECTOR}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '1.6rem',
                      fontWeight: 400,
                    }
                  }}
                  onClick={(e) => {
                    logAnalyticsEvent(AnalyticsEvent.FILTER_PROPERTY, { filterType: FilterType.MANAGER })
                    setOpenFilter(FilterType.MANAGER)
                    action(e)
                  }}
                  customInputContent={!!selectedManager &&
                    <Stack
                      flexDirection="row"
                      paddingRight="0.4rem"
                      fontSize="1.4rem"
                      alignItems="center"
                      paddingTop="0.1rem"
                      color={GRAY_900}
                      onClick={handleRemoveManager}
                      sx={{ cursor: 'pointer', ':hover': { color: CORAL_700 } }}
                    >
                      <DeleteOutlineOutlined fontSize="inherit" />
                    </Stack>
                  }
                />
              )}
            >
              <Box width="20.5rem">
                {managersList && managersList.map(({ id, name }) =>
                  <MUIDropdownItem key={id} onClick={() => handleChangeManager(name, id)}>
                    <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                      {name}
                    </Typography>
                  </MUIDropdownItem>
                )}
              </Box>
            </MUIDropdown>
          </Stack>
        )}

      </Stack>

    </ResponsiveDrawer>
  )
}
